import { TIME_WINDOW_PRESETS } from "@repo/api";
import type { ProtocolDefinitionDto } from "@repo/data-store";
import type { DragHandleProps } from "@repo/ui/components/kibo-ui/list";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { Clock, GripVertical, Trash2 } from "lucide-react";
import type { ProtocolFormValues } from "@/features/catalog/forms/use-plan-definition-form";

interface ProtocolCardProps {
  session: ProtocolFormValues;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  dragHandleProps?: DragHandleProps;
  protocolPlanDefinitions?: ProtocolDefinitionDto[];
}

export function ProtocolCard({
  session,
  isSelected,
  onSelect,
  onRemove,
  dragHandleProps,
  protocolPlanDefinitions = [],
}: ProtocolCardProps) {
  const pd = protocolPlanDefinitions.find((candidate) => candidate.definitionUrl === session.definitionCanonical);
  // The protocol projection already totals its steps; don't re-walk them here.
  const durationMinutes = pd?.totalDurationMinutes ?? 0;
  const displayTitle = pd?.name || "Protocol";
  const windowLabel = session.timeWindow
    ? (TIME_WINDOW_PRESETS[session.timeWindow]?.shortLabel ?? session.timeWindow)
    : null;

  return (
    <div
      className={cn(
        "flex items-center justify-between px-3 h-10 text-sm transition-colors",
        isSelected && "bg-muted/50",
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        {dragHandleProps && (
          <div ref={dragHandleProps.ref} className="cursor-grab text-muted-foreground hover:text-foreground shrink-0">
            <GripVertical className="size-4" />
          </div>
        )}
        <button type="button" className="font-medium text-left cursor-pointer truncate" onClick={onSelect}>
          {displayTitle}
        </button>
      </div>

      <div className="flex items-center gap-4 text-muted-foreground shrink-0">
        {windowLabel && <span>{windowLabel}</span>}
        {durationMinutes > 0 && (
          <span className="flex items-center gap-1 text-xs">
            <Clock className="size-3" />
            {durationMinutes} min
          </span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="size-6 text-destructive hover:text-destructive"
          onClick={onRemove}
        >
          <Trash2 className="size-3" />
        </Button>
      </div>
    </div>
  );
}
